import { errorIsStatus, useClientPortalAPI } from "@certn/api";
import {
  Avatar,
  BasicButton,
  ColumnDefinition,
  Container,
  DataTable,
  NeutralTag,
  Pagination,
  SectionMessage,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate, parseName } from "@certn/utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";
import { StatusFilter } from "./components/StatusFilter";
import { useCredentialVerificationTemplatesInPackages } from "./hooks";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { Schemas } from "../../types";
import { queryParamsToApiParams } from "../utils";

function StatusTag({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation();

  if (isActive) {
    return (
      <SuccessTag
        testID="credential-verification-template-active-tag"
        type="subtle"
      >
        {t("Active")}
      </SuccessTag>
    );
  }

  return (
    <NeutralTag testID="credential-verification-deactivated-tag" type="subtle">
      {t("Deactivated")}
    </NeutralTag>
  );
}

export function CredentialVerificationTemplateListView() {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const navigate = useNavigate();
  const { data, isLoading, error, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/credential-verification/`,
    queryParamsToApiParams(params)
  );

  const templateIdsBelongingToActivePackages =
    useCredentialVerificationTemplatesInPackages();

  const activeTemplates = data?.results.filter(
    (template) => template.is_active === true
  );

  const columns: ColumnDefinition<
    Schemas["CredentialVerificationTemplateList"]
  >[] = [
    {
      key: "name",
      name: t("Name"),
      isRowHeader: true,
      allowsSorting: true,
      testID: "name-column-header",
      render({ name }: { name: string }) {
        return (
          <Text
            style="form/body-secondary"
            testID="credential-verification-template-name"
          >
            {name}
          </Text>
        );
      },
    },
    {
      key: "is_active",
      name: t("Status"),
      isRowHeader: true,
      allowsSorting: true,
      testID: "status-column-header",
      render({ is_active }: { is_active: boolean }) {
        return <StatusTag isActive={is_active} />;
      },
    },
    {
      key: "usage_count",
      name: t("Usage"),
      isRowHeader: true,
      testID: "usage-count-column-header",
      render({ usage_count }: { usage_count: number }) {
        return (
          <Text
            style="form/body-secondary"
            testID="credential-verification-template-usage-count"
          >
            {`${usage_count} cases`}
          </Text>
        );
      },
    },
    {
      key: "created",
      name: t("Created date"),
      isRowHeader: true,
      allowsSorting: true,
      testID: "created-date-column-header",
      render({ created }: { created: string }) {
        return (
          <Text style="form/body-secondary" testID="created-date">
            {formatDate(created, "MMM dd, yyyy")}
          </Text>
        );
      },
    },
    {
      key: "created_by",
      name: t("Created by"),
      isRowHeader: true,
      testID: "created-by-column-header",
      render(item: Schemas["CredentialVerificationTemplateList"]) {
        if (item.created_by) {
          return (
            <Avatar
              size="small"
              name={parseName(item.created_by.auth0_full_name || undefined)}
            />
          );
        }
      },
    },
    {
      key: "actions",
      isAction: true,
      width: 48,
      render(template: Schemas["CredentialVerificationTemplateList"]) {
        return (
          <ActionsDropdown
            templateIdsBelongingToActivePackages={
              templateIdsBelongingToActivePackages
            }
            numActiveTemplates={activeTemplates?.length || 0}
            mutate={mutate}
            templateId={template.id}
            name={template.name}
            isActive={template.is_active}
          />
        );
      },
    },
  ];

  return (
    <Container layout="full-width">
      <PageTitle
        actions={
          <BasicButton
            testID="new-template-button"
            onPress={() =>
              navigate("/settings/credential-verification-templates/create")
            }
          >
            {t("New template")}
          </BasicButton>
        }
      >
        {t("Credential verification")}
      </PageTitle>
      <FilterBar hasSearch testID="credential-verification">
        <StatusFilter />
      </FilterBar>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to retrieve templates")}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <DataTable
        isLoading={isLoading}
        onSortChange={(sortDescriptor) =>
          setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
        }
        onRowAction={(id) => {
          navigate(`/settings/credential-verification-templates/${id}`);
        }}
        sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
        columns={columns}
        rows={data?.results ? data.results : []}
        aria-label={t("Credential Verification Templates Table")}
        testId="credential-verification-template-list-view-data-table"
      />
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
}
